import React, { useMemo, useState } from "react";

function AuctionPerformancePage({ dateFilteredAuctions }) {
  const [searchText, setSearchText] = useState("");

  const formatKWD = (value) => `${Number(value || 0).toFixed(3)} KWD`;
  const formatClosureReasonLabel = (reason) => {
    const labels = {
      winner_declared: "Winner Declared",
      creator_closed: "Closed By Creator",
      time_expired: "Expired By Time Limit",
      admin_closed: "Closed By Admin",
      closed_unknown: "Closed - Unknown Reason",
      not_closed: "Not Closed",
    };

    return labels[reason] || reason || "—";
  };

  const getTime = (value) => {
    if (!value) return 0;
    if (value?.seconds) return value.seconds * 1000;
    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);
    if (!time) return "—";
    return new Date(time).toLocaleString();
  };

  const performance = useMemo(() => {
    const totalAuctions = dateFilteredAuctions.length;

    const completedAuctions = dateFilteredAuctions.filter(
      (auction) => auction.status === "closed"
    ).length;

    const auctionsWithWinners = dateFilteredAuctions.filter(
      (auction) => auction.currentWinner || auction.winner
    ).length;

    const totalRevenue = dateFilteredAuctions.reduce(
      (sum, auction) =>
        sum +
        Number(auction.billingAmountKWD || 0) +
        Number(auction.extensionTotalAmountKWD || 0),
      0
    );

    const totalBidRecords = dateFilteredAuctions.reduce(
      (sum, auction) => sum + Number(auction.bidHistory?.length || 0),
      0
    );

    const categoryMap = {};
    const durationMap = {};
    const statusMap = {};
    const closureReasonMap = {};

    dateFilteredAuctions.forEach((auction) => {
      const category = auction.category || "Uncategorized";
      const duration = auction.durationMinutes
        ? `${auction.durationMinutes} min`
        : "Unknown";
      const status = auction.status || "unknown";
      const closureReason =
      auction.closureReason ||
      (auction.status === "closed" ? "closed_unknown" : "not_closed");

      if (!categoryMap[category]) {
        categoryMap[category] = {
          category,
          auctions: 0,
          closed: 0,
          withWinners: 0,
          revenue: 0,
          bidRecords: 0,
        };
      }

      categoryMap[category].auctions += 1;
      categoryMap[category].closed += auction.status === "closed" ? 1 : 0;
      categoryMap[category].withWinners +=
        auction.currentWinner || auction.winner ? 1 : 0;
      categoryMap[category].revenue +=
        Number(auction.billingAmountKWD || 0) +
        Number(auction.extensionTotalAmountKWD || 0);
      categoryMap[category].bidRecords += Number(auction.bidHistory?.length || 0);

      if (!durationMap[duration]) {
        durationMap[duration] = {
          duration,
          auctions: 0,
          closed: 0,
          withWinners: 0,
          revenue: 0,
          bidRecords: 0,
        };
      }

      durationMap[duration].auctions += 1;
      durationMap[duration].closed += auction.status === "closed" ? 1 : 0;
      durationMap[duration].withWinners +=
        auction.currentWinner || auction.winner ? 1 : 0;
      durationMap[duration].revenue +=
        Number(auction.billingAmountKWD || 0) +
        Number(auction.extensionTotalAmountKWD || 0);
      durationMap[duration].bidRecords += Number(auction.bidHistory?.length || 0);

      if (!statusMap[status]) {
        statusMap[status] = {
          status,
          auctions: 0,
        };
      }

      statusMap[status].auctions += 1;
      if (!closureReasonMap[closureReason]) {
        closureReasonMap[closureReason] = {
          closureReason,
          auctions: 0,
        };
      }

      closureReasonMap[closureReason].auctions += 1;
    });

    const categoryRows = Object.values(categoryMap).sort(
      (a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)
    );

    const durationRows = Object.values(durationMap).sort(
      (a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)
    );

    const statusRows = Object.values(statusMap).sort(
      (a, b) => Number(b.auctions || 0) - Number(a.auctions || 0)
    );

    const closureReasonRows = Object.values(closureReasonMap).sort(
      (a, b) => Number(b.auctions || 0) - Number(a.auctions || 0)
    );

    const topCategory = categoryRows[0]?.category || "—";

    const topAuctions = [...dateFilteredAuctions]
      .sort(
        (a, b) =>
          Number(b.currentBid || 0) -
            Number(a.currentBid || 0) ||
          Number(b.bidHistory?.length || 0) - Number(a.bidHistory?.length || 0)
      )
      .slice(0, 10);

    const lowAuctions = dateFilteredAuctions
      .filter(
        (auction) =>
          Number(auction.currentBid || 0) === 0 &&
          Number(auction.bidHistory?.length || 0) === 0
      )
      .slice(0, 20);

    return {
      totalAuctions,
      completedAuctions,
      auctionsWithWinners,
      totalRevenue,
      totalBidRecords,
      averageRevenuePerAuction:
        totalAuctions > 0 ? totalRevenue / totalAuctions : 0,
      averageBidsPerAuction:
        totalAuctions > 0 ? totalBidRecords / totalAuctions : 0,
      topCategory,
      categoryRows,
      durationRows,
      statusRows,
      closureReasonRows,
      topAuctions,
      lowAuctions,
    };
  }, [dateFilteredAuctions]);

  const filteredTopAuctions = performance.topAuctions.filter((auction) => {
    const search = searchText.toLowerCase().trim();
    if (!search) return true;

    return (
      String(auction.id || "").toLowerCase().includes(search) ||
      String(auction.title || "").toLowerCase().includes(search) ||
      String(auction.createdByUsername || "").toLowerCase().includes(search) ||
      String(auction.createdBy || "").toLowerCase().includes(search) ||
      String(auction.category || "").toLowerCase().includes(search)
    );
  });

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Total Auctions</span>
          <strong>{performance.totalAuctions}</strong>
        </div>

        <div className="stat-card">
          <span>Completed Auctions</span>
          <strong>{performance.completedAuctions}</strong>
        </div>

        <div className="stat-card">
          <span>Auctions With Winners</span>
          <strong>{performance.auctionsWithWinners}</strong>
        </div>

        <div className="stat-card">
          <span>Average Revenue / Auction</span>
          <strong>{formatKWD(performance.averageRevenuePerAuction)}</strong>
        </div>

        <div className="stat-card">
          <span>Average Bid Records / Auction</span>
          <strong>{Number(performance.averageBidsPerAuction || 0).toFixed(1)}</strong>
        </div>

        <div className="stat-card">
          <span>Top Category</span>
          <strong>{performance.topCategory}</strong>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Category Performance</h2>
            <p>Shows which categories create the most activity and revenue.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Auctions</th>
                <th>Closed</th>
                <th>With Winners</th>
                <th>Total Revenue</th>
                <th>Bid Records</th>
              </tr>
            </thead>

            <tbody>
              {performance.categoryRows.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td>{row.auctions}</td>
                  <td>{row.closed}</td>
                  <td>{row.withWinners}</td>
                  <td>{formatKWD(row.revenue)}</td>
                  <td>{row.bidRecords}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Duration Performance</h2>
            <p>Compares performance by auction duration.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Duration</th>
                <th>Auctions</th>
                <th>Closed</th>
                <th>With Winners</th>
                <th>Total Revenue</th>
                <th>Bid Records</th>
              </tr>
            </thead>

            <tbody>
              {performance.durationRows.map((row) => (
                <tr key={row.duration}>
                  <td>{row.duration}</td>
                  <td>{row.auctions}</td>
                  <td>{row.closed}</td>
                  <td>{row.withWinners}</td>
                  <td>{formatKWD(row.revenue)}</td>
                  <td>{row.bidRecords}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Status Performance</h2>
            <p>Summary of auction status distribution.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
            <tr>
                <th>Status</th>
                <th>Closure Reason</th>
                <th>Auctions</th>
              </tr>
            </thead>

            <tbody>
              {performance.statusRows.map((row) => (
                <tr key={row.status}>
                                    <td>
                    <span className={`badge ${row.status || "unknown"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="small-muted">—</td>
                  <td>{row.auctions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
          <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Closure Reason Performance</h2>
            <p>Shows how auctions are being closed.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Closure Reason</th>
                <th>Auctions</th>
              </tr>
            </thead>

            <tbody>
              {performance.closureReasonRows.map((row) => (
                <tr key={row.closureReason}>
                                    <td>{formatClosureReasonLabel(row.closureReason)}</td>
                  <td>{row.auctions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
            <h2>Top Performing Auctions</h2>
            <p>Auctions ranked by current bid and bid records.</p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search auction ID, title, seller, email, or category..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Auction ID</th>
                <th>Title</th>
                <th>Category</th>
                <th>Seller</th>
                <th>Status</th>
                <th>Current Bid</th>
                <th>Bid Records</th>
                <th>Winner</th>
                <th>Created At</th>
              </tr>
            </thead>

            <tbody>
              {filteredTopAuctions.map((auction) => (
                <tr key={auction.id}>
                  <td className="small-muted">{auction.id}</td>
                  <td>{auction.title || "Untitled"}</td>
                  <td>{auction.category || "—"}</td>
                  <td>{auction.createdByUsername || auction.createdBy || "—"}</td>
                  <td>
                    <span className={`badge ${auction.status || "unknown"}`}>
                      {auction.status || "unknown"}
                    </span>
                  </td>
                  <td>{formatKWD(auction.currentBid)}</td>
                  <td>{Number(auction.bidHistory?.length || 0)}</td>
                  <td>{auction.currentWinner || auction.winner || "—"}</td>
                  <td>{formatDate(auction.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Low Performing Auctions</h2>
            <p>Auctions with no current bid and no stored bid records.</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Auction ID</th>
                <th>Title</th>
                <th>Category</th>
                <th>Seller</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>

            <tbody>
              {performance.lowAuctions.map((auction) => (
                <tr key={auction.id}>
                  <td className="small-muted">{auction.id}</td>
                  <td>{auction.title || "Untitled"}</td>
                  <td>{auction.category || "—"}</td>
                  <td>{auction.createdByUsername || auction.createdBy || "—"}</td>
                  <td>
                    <span className={`badge ${auction.status || "unknown"}`}>
                      {auction.status || "unknown"}
                    </span>
                  </td>
                  <td>{formatDate(auction.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export default AuctionPerformancePage;